import network from '@/services/network';

/**
 * PDF-Vorlagen der Antragserstellung (BER-67). `list` liefert alle möglichen
 * Dokumenttypen mit (ggf.) hochgeladenem Template; Upload/Löschen sind
 * SA-only (application-templates.manage). `generate` erzeugt die befüllten
 * Dokumente und liefert sie als Datei-Blob (PDF oder ZIP).
 */
export interface ApplicationTemplate {
  id: number;
  document_type: string;
  document_type_label: string;
  original_name: string;
  field_count: number;
  uploaded_by?: string | null;
  updated_at?: string;
}

/** Ein Dokumenttyp inkl. dem (optional) hochgeladenen Template. */
export interface ApplicationTemplateSlot {
  document_type: string;
  label: string;
  template: ApplicationTemplate | null;
}

/** Ein zu erzeugendes Dokument: Typ + VN-Stammdaten (wie im Wizard editiert). */
export interface GenerateDocumentInput {
  document_type: string;
  vn: Record<string, string>;
}

/** Ein Vermittler für das Beratungsdoku-Dropdown (Name + Nummer). */
export interface Vermittler {
  name: string;
  nummer: string;
  /** inSign-Zuweisungsadresse der Firmen-Unterschriften; null = keine Zuweisung. */
  email: string | null;
}

/** Ein Antrag für die Sparten-Ableitung der Beratungsdoku (Backend). */
export interface BeratungsdokuAntrag {
  typ: string;
  besparen_bis: number | null;
  anbieter: string;
  /** Anlageziel (Sparte) des Vertrags — gewählt oder automatisch abgeleitet. */
  sparte: string;
  /** Kunde folgt der Empfehlung — je Vertrag. */
  folgt_empfehlung: boolean;
  /** Gewünschte Änderungen, wenn der Kunde der Empfehlung nicht folgt. */
  aenderung: string;
}

/** Beratungsdoku-Payload (BER-67): Parameter + Kunde + Antragsliste. */
export interface BeratungsdokuInput {
  beratungsart: string;
  datum: string;
  vermittlernummer: string;
  kunde: Record<string, string>;
  antraege: BeratungsdokuAntrag[];
}

const list = async (): Promise<ApplicationTemplateSlot[]> => {
  const { data } = await network.get<{ data: ApplicationTemplateSlot[] }>('/application-templates');
  return data.data;
};

const upload = async (documentType: string, file: File): Promise<ApplicationTemplate> => {
  const form = new FormData();
  form.append('document_type', documentType);
  form.append('file', file);
  const { data } = await network.post<{ data: ApplicationTemplate }>(
    '/application-templates',
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data.data;
};

const remove = async (id: number): Promise<void> => {
  await network.delete(`/application-templates/${id}`);
};

/** Der beim Erstellen angelegte Vorgang (Dokumente-Tab). */
export interface ErstellterVorgang {
  id: number;
  status: string;
  files: { id: number; name: string }[];
  /** Nicht-fatale Probleme (z. B. softfair-Fallback auf die Muster-PDF). */
  warnings?: string[];
}

/**
 * Daten EINES Wizard-Antrags für die echte softfair-Antragserstellung
 * (Berechnung → Dokumentanforderung). Tarif + Typ referenzieren die im Admin
 * gepflegte softfair-Konfiguration.
 */
export interface SoftfairAntragInput {
  /** Eindeutige Konfigurations-ID (null = Fallback über tarif_id+typ). */
  konfig_id: number | null;
  tarif_id: string;
  typ: string;
  beruf_id: number | null;
  /** Politisch exponierte Person (fürs GwG-Formular). */
  pep: boolean;
  vertrag: { start: string; sparrate: string; bezahlung: string; ende: string; iban: string };
  vp: Record<string, string>;
  vn: Record<string, string>;
  /**
   * Rechtsnachfolger bei Tod des VN (Allianz-Kindersparplan, Sonderfall 5):
   * leere Felder, wenn nicht relevant (Backend füllt dann keine _RECHTSNF-
   * Felder). Anrede/Name/Adresse/Geburtsdatum wie im Allianz-Formular.
   */
  rechtsnachfolger?: {
    salutation: string;
    first_name: string;
    last_name: string;
    address: string;
    postal_code: string;
    city: string;
    date_of_birth: string;
  };
  /**
   * Abweichender Beitragszahler (≠ VN, 12.09.2026): geht als abweichender
   * Kontoinhaber der Bankverbindung an softfair. Fehlt/undefined, wenn nicht
   * aktiviert.
   */
  beitragszahler?: {
    salutation: string;
    first_name: string;
    last_name: string;
    address: string;
    postal_code: string;
    city: string;
    date_of_birth: string;
    // GwG-/Ausweis-Legitimationsdaten (fürs Legitimations-Schreiben).
    birthplace: string;
    nationality: string;
    id_document_type: string;
    id_document_number: string;
    id_document_authority: string;
    id_document_valid_until: string;
    pep: string;
  };
}

/**
 * Erzeugt die Dokumente für einen Kunden. Kein Download mehr: das Backend legt
 * einen VORGANG am Mandanten an (Status „Erstellt"), die Dateien erscheinen im
 * Dokumente-Tab.
 */
/**
 * AXA-Sonderfall (10.09.2026): „Bestätigung des gesetzlichen Vertreters" —
 * je VN ein Schreiben mit den Kindern, deren Nachname abweicht (Daten aus
 * sonderfaelle.ts, das PDF generiert das Backend in den Vorgang).
 */
export interface VertreterBestaetigungInput {
  vn: { first_name: string; last_name: string; date_of_birth: string };
  kinder: { vorname: string; nachname: string; geburtsdatum: string }[];
}

/**
 * AXA-Sonderfall (10.09.2026): „Abweichende Adresse der versicherten Person"
 * — je VN ein Schreiben mit den Kindern, deren Meldeadresse abweicht.
 */
export interface AbweichendeAdresseInput {
  vn: { first_name: string; last_name: string; adresse: string };
  kinder: { name: string; adresse: string }[];
}

/**
 * Zuzahlungs-Schreiben (10.09.2026): je Antrag mit im Wizard angegebener
 * Zuzahlung zu Vertragsbeginn — das PDF generiert das Backend in den
 * Vorgang (Text admin-editierbar).
 */
export interface ZuzahlungInput {
  vn: {
    first_name: string;
    last_name: string;
    // Nur AXA-Formular: volle VN-Stammdaten.
    address?: string;
    postal_code?: string;
    city?: string;
    date_of_birth?: string;
    birthplace?: string;
    nationality?: string;
  };
  vp_name: string;
  /** Formatierter Betrag (z. B. „5.000€"). */
  betrag: string;
  /** true = AXA: offizielles AXA-Formular befüllen statt freiem Schreiben. */
  ist_axa?: boolean;
  /** „mit Wirkung zum": Monat/Jahr des Vertragsbeginns (AXA-Formular). */
  wirkung_monat?: string;
  wirkung_jahr?: string;
  /** Berufliche Tätigkeit der VP (AXA-Formular). */
  beruf?: string;
}

/**
 * Korrekturschreiben (AXA-Sonderfall, 10.09.2026): je Antrag mit nicht
 * abbildbarem Bezugsrecht im Todesfall ein Word-Schreiben (fehlerhaft =
 * AXA-Standard „Eltern", korrekt = gewähltes Bezugsrecht).
 */
export interface KorrekturschreibenInput {
  gesellschaft: string;
  vn_name: string;
  vn_nachname: string;
  vp_name: string;
  punkte: { fehler: string; korrekt: string }[];
}

/**
 * AXA-Auslandsklausel (Sonderfall, 10.09.2026): je VN mit Nicht-EU-
 * Nationalität ein befülltes AXA-Formular „Erklärung Auslandsklausel"
 * (nur Kopfdaten; Risiko-Fragen füllt der Kunde/Berater).
 */
export interface AuslandsklauselInput {
  vn: {
    first_name: string;
    last_name: string;
    date_of_birth: string;
    address: string;
    postal_code: string;
    city: string;
  };
  /** Antworten der Risiko-Fragen (Schlüssel aus auslandsklausel/felder.ts). */
  antworten: Record<string, string>;
}

const generate = async (
  customerId: number,
  documents: GenerateDocumentInput[],
  idd?: Record<string, string>,
  beratungsdoku?: BeratungsdokuInput,
  antraegeSoftfair?: SoftfairAntragInput[],
  beratungsdatum?: string,
  nachweise?: { adressnachweis: boolean; gehaltsnachweis: boolean },
  vertreterBestaetigungen?: VertreterBestaetigungInput[],
  zuzahlungen?: ZuzahlungInput[],
  korrekturschreiben?: KorrekturschreibenInput[],
  abweichendeAdressen?: AbweichendeAdresseInput[],
  auslandsklauseln?: AuslandsklauselInput[],
  sparratenNachweis?: { name: string; base64: string },
): Promise<ErstellterVorgang> => {
  const { data } = await network.post<{ message: string; data: ErstellterVorgang }>(
    `/customers/${customerId}/application-documents`,
    {
      documents,
      idd,
      beratungsdoku,
      antraege_softfair: antraegeSoftfair,
      // Beratungsdatum von Wizard-Seite 1 — Stichtag ALLER Dokumente
      // (IDD, Maklermandat, Beratungsprotokoll-Kopf).
      beratungsdatum,
      // Nachweis-Pflichten (Wizard-Berechnung) → Dropzones am Vorgang.
      braucht_adressnachweis: nachweise?.adressnachweis ?? false,
      braucht_gehaltsnachweis: nachweise?.gehaltsnachweis ?? false,
      // AXA-Sonderfall: Vertreter-Bestätigung(en), leer = keine nötig.
      vertreter_bestaetigungen: vertreterBestaetigungen,
      // Zuzahlungs-Schreiben je Antrag mit angegebener Zuzahlung.
      zuzahlungen,
      // Korrekturschreiben (Word) je AXA-Antrag mit abweichendem Bezugsrecht.
      korrekturschreiben,
      // AXA-Schreiben je VN mit Kind, dessen Adresse vom VN abweicht.
      abweichende_adressen: abweichendeAdressen,
      // AXA-Auslandsklausel je VN mit Nicht-EU-Nationalität.
      auslandsklauseln,
      // Sparraten-Nachweis (Base64), falls die 10 %-Grenze überschritten und
      // ein Nachweis hochgeladen wurde — landet als Dokument im Vorgang.
      sparraten_nachweis: sparratenNachweis,
    },
  );
  return data.data;
};

/** Lädt die Vermittler-Liste für das Beratungsdoku-Dropdown (Name + Nummer). */
/** Vermittler-Liste + am Berater-Account gespeicherte Vorbelegung. */
export interface VermittlerListe {
  liste: Vermittler[];
  /** Zuletzt beim Dokumente-Erzeugen verwendete Nummer; null beim ersten Mal. */
  vorbelegung: string | null;
}

const vermittler = async (): Promise<VermittlerListe> => {
  const { data } = await network.get<{ data: Vermittler[]; vorbelegung: string | null }>(
    '/application-documents/vermittler',
  );
  return { liste: data.data, vorbelegung: data.vorbelegung };
};

/** Ergebnis der Ausweis-Analyse: erkannte VN-Stammdaten + Meta. */
export interface AusweisAnalyseErgebnis {
  /** Wizard-Stammdaten-Felder (first_name, last_name, date_of_birth, id_document_*, …). */
  felder: Record<string, string>;
  lesbar: boolean;
  warnung: string;
}

/**
 * Liest ein hochgeladenes Ausweisdokument per Claude aus und liefert die
 * erkannten VN-Stammdaten zurück. Ein oder zwei Dateien (Vorder-/Rückseite).
 */
const analyzeId = async (dateien: File[]): Promise<AusweisAnalyseErgebnis> => {
  const form = new FormData();
  dateien.forEach((datei) => form.append('files[]', datei));
  const { data } = await network.post<{ data: AusweisAnalyseErgebnis }>(
    '/application-documents/analyze-id',
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data.data;
};

/** Ergebnis der Boniprüfung des Mandanten (BER-67). */
export interface BonicheckErgebnis {
  status: 'positiv' | 'negativ';
  checked_at: string;
  /** true = gespeichertes Ergebnis wiederverwendet (keine neue Prüfung). */
  aus_cache: boolean;
}

/**
 * Boniprüfung des Mandanten: läuft automatisch in der Wizard-Zusammenfassung
 * bei Gesellschaften mit `braucht_bonicheck`. Ein bereits gespeichertes
 * Ergebnis wird wiederverwendet (keine erneute kostenpflichtige Prüfung).
 */
const bonicheck = async (customerId: number): Promise<BonicheckErgebnis> => {
  const { data } = await network.post<{ data: BonicheckErgebnis }>(
    `/customers/${customerId}/bonicheck`,
  );
  return data.data;
};

/** Ergebnis der IBAN-Prüfung (DB-Check + ibantest.com). */
export interface IbanCheckErgebnis {
  valid: boolean;
  source: string;
  error?: string;
}

/** Prüft eine IBAN (erst bekannter Kunde in der DB, dann ibantest.com). */
const checkIban = async (iban: string): Promise<IbanCheckErgebnis> => {
  const { data } = await network.post<IbanCheckErgebnis>(
    '/application-documents/check-iban',
    { iban },
  );
  return data;
};

/** Nachweis-Typen der Vorgangs-Dropzones. */
export type NachweisTyp = 'adressnachweis' | 'gehaltsnachweis';

/** Ein Antrags-Vorgang im Dokumente-Tab: Status + erzeugte Dokumente. */
export interface ApplicationProcess {
  id: number;
  /** Frei vergebbarer Name (umbenennbar); null = Anzeige „Vorgang #<Id>". */
  name: string | null;
  status: string;
  status_label: string;
  created_at: string | null;
  /** true = bereits an den Paten ausgehändigt (kein erneutes Senden möglich). */
  an_paten_gesendet: boolean;
  /** Vom Wizard berechnete Nachweis-Pflichten (steuern die Dropzones). */
  braucht_adressnachweis: boolean;
  braucht_gehaltsnachweis: boolean;
  documents: {
    id: number;
    name: string;
    file_size: number | null;
    /** Gesetzt bei über die Dropzone hochgeladenen Nachweisen. */
    nachweis_typ: NachweisTyp | null;
  }[];
}

/** Vorgangs-Liste + ob inSign eingebettet (iframe) geöffnet werden darf. */
export interface ApplicationProcessListe {
  vorgaenge: ApplicationProcess[];
  /**
   * true nur, wenn die inSign-Instanz ihr Session-Cookie mit SameSite=None
   * setzt (Server-Konfiguration) — sonst verliert der iframe die Session
   * und der Button öffnet inSign in einem neuen Tab.
   */
  insignEmbed: boolean;
}

/** Listet die Antrags-Vorgänge eines Mandanten (neueste zuerst). */
const processes = async (customerId: number): Promise<ApplicationProcessListe> => {
  const { data } = await network.get<{ data: ApplicationProcess[]; insign_embed: boolean }>(
    `/customers/${customerId}/application-processes`,
  );
  return { vorgaenge: data.data, insignEmbed: data.insign_embed };
};

/** Benennt einen Vorgang um (leer = zurück zur Standardanzeige). */
const renameProcess = async (
  customerId: number,
  processId: number,
  name: string,
): Promise<string | null> => {
  const { data } = await network.patch<{ data: { name: string | null } }>(
    `/customers/${customerId}/application-processes/${processId}`,
    { name },
  );
  return data.data.name;
};

/** Löscht einen Vorgang samt erzeugter Dokumente (inkl. Dateien). */
const removeProcess = async (customerId: number, processId: number): Promise<void> => {
  await network.delete(`/customers/${customerId}/application-processes/${processId}`);
};

/** Lädt einen Nachweis (Adress-/Gehaltsnachweis) an einen Vorgang hoch. */
const uploadNachweis = async (
  customerId: number,
  processId: number,
  typ: NachweisTyp,
  file: File,
): Promise<ApplicationProcess['documents'][number]> => {
  const form = new FormData();
  form.append('typ', typ);
  form.append('file', file);
  const { data } = await network.post<{ data: ApplicationProcess['documents'][number] }>(
    `/customers/${customerId}/application-processes/${processId}/nachweis`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data.data;
};

/** Lädt alle Dokumente eines Vorgangs als ZIP. */
const downloadProcessZip = async (customerId: number, processId: number): Promise<Blob> => {
  const { data } = await network.get<Blob>(
    `/customers/${customerId}/application-processes/${processId}/download`,
    { responseType: 'blob' },
  );
  return data;
};

/** Ergebnis des inSign-Öffnens: Einstiegs-URL + (ggf. gewechselter) Status. */
export interface InsignSession {
  url: string;
  /** Upload zu inSign setzt „Erstellt" auf „Warte auf Kundenunterschrift". */
  status: string;
  statusLabel: string;
}

/**
 * Öffnet die inSign-Signatur-Session eines Vorgangs und liefert die
 * Einstiegs-URL. ACHTUNG: One-Time-URL — jede URL ist genau einmal öffenbar;
 * für jeden Einstieg (iframe, neuer Tab) einen frischen Aufruf machen.
 */
const insignUrl = async (customerId: number, processId: number): Promise<InsignSession> => {
  const { data } = await network.post<{ url: string; status: string; status_label: string }>(
    `/customers/${customerId}/application-processes/${processId}/insign`,
  );
  return { url: data.url, status: data.status, statusLabel: data.status_label };
};

/**
 * Übergibt den Vorgang an den Paten: inSign händigt die Session aus und
 * verschickt den Signatur-Link per E-Mail an die hinterlegte Paten-Adresse.
 * NICHT umkehrbar — danach kein Berater-Einstieg in die Session mehr.
 */
const anPatenSenden = async (
  customerId: number,
  processId: number,
): Promise<{ status: string; statusLabel: string }> => {
  const { data } = await network.post<{
    data: { status: string; status_label: string; an_paten_gesendet: boolean };
  }>(`/customers/${customerId}/application-processes/${processId}/an-paten`);
  return { status: data.data.status, statusLabel: data.data.status_label };
};

/** Unterschrifts-Fortschritt eines Vorgangs aus inSign (Polling im Dokumente-Tab). */
export interface InsignSignaturStatus {
  /** Pflicht-Unterschriften insgesamt. */
  gesamt: number;
  /** Davon noch offen. */
  offen: number;
  /** Vorgangs-Status nach der Abfrage (Backend schaltet die Signatur-Phase um). */
  status: string;
  statusLabel: string;
}

/**
 * Fragt den Unterschrifts-Stand der inSign-Session ab. null, wenn der Vorgang
 * (noch) keine Session hat oder inSign nicht erreichbar ist — dann einfach
 * nichts anzeigen und beim nächsten Poll erneut versuchen.
 */
const insignStatus = async (
  customerId: number,
  processId: number,
): Promise<InsignSignaturStatus | null> => {
  const { data } = await network.get<{
    data: { gesamt: number; offen: number; status: string; status_label: string } | null;
  }>(`/customers/${customerId}/application-processes/${processId}/insign-status`);
  if (!data.data) return null;
  return {
    gesamt: data.data.gesamt,
    offen: data.data.offen,
    status: data.data.status,
    statusLabel: data.data.status_label,
  };
};

const applicationTemplate = {
  list, upload, remove, generate, vermittler, analyzeId, checkIban, bonicheck,
  processes, removeProcess, renameProcess, downloadProcessZip, insignUrl, insignStatus, anPatenSenden, uploadNachweis,
};

export default applicationTemplate;
